import { z } from '@hono/zod-openapi'

export const DocumentId = z.string().uuid().openapi({ example: 'doc-1234-5678', description: 'Branded document identifier' })
export type DocumentId = z.infer<typeof DocumentId> & { readonly __brand: 'DocumentId' }

export const DocumentVersionId = z.string().uuid().openapi({ example: 'ver-1234-5678', description: 'Branded document version identifier' })
export type DocumentVersionId = z.infer<typeof DocumentVersionId> & { readonly __brand: 'DocumentVersionId' }

export const TemplateId = z.string().uuid().openapi({ example: 'tpl-1234-5678', description: 'Branded template identifier' })
export type TemplateId = z.infer<typeof TemplateId> & { readonly __brand: 'TemplateId' }

export const DocumentTypeSchema = z.enum([
  'iep', 'iep-amendment', 'evaluation', 'eval-report', 'consent',
  's504-plan', 's504-evaluation', 'mtss-plan', 'health-plan',
  'pwn', 'referral', 'eligibility', 'manifestation',
  'transcript', 'progress-report', 'notice',
]).openapi({ example: 'iep', description: 'Legal document type in the CetusEd canon' })

export const DocumentClassificationSchema = z.enum([
  'confidential', 'internal', 'public', 'ferpa-restricted',
]).openapi({ example: 'ferpa-restricted', description: 'Document classification determining access and handling controls' })

export const DocumentStatusSchema = z.enum([
  'draft', 'generated', 'translated', 'reviewed', 'signed', 'archived',
]).openapi({ example: 'draft', description: 'Document lifecycle status — follows draft → generated → translated → reviewed → signed → archived' })

export const DocumentVersionStatusSchema = z.enum([
  'pending', 'generated', 'translated', 'reviewed', 'approved',
]).openapi({ example: 'generated' })

export const DocumentVersionSchema = z.object({
  id: DocumentVersionId,
  documentId: DocumentId,
  version: z.number().int().min(1).openapi({ example: 1 }),
  language: z.string().min(2).max(10).openapi({ example: 'en', description: 'BCP-47 language code' }),
  sourceVersion: z.number().int().min(1).nullable().openapi({ description: 'Source version number for translated versions' }),
  hash: z.string().length(64).openapi({ example: 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855' }),
  storageLocation: z.string().min(1).max(1024).openapi({ example: 's3://cetused-documents/dist-wa-12345/iep-1234-v1.pdf' }),
  size: z.number().int().min(0).openapi({ example: 245760 }),
  mimeType: z.string().openapi({ example: 'application/pdf' }),
  createdBy: z.string().uuid(),
  createdAt: z.string().datetime(),
  status: DocumentVersionStatusSchema,
}).openapi('DocumentVersion', { description: 'A specific version of a document, including translated language variants' })

export const TemplateVariableSchema = z.object({
  name: z.string().min(1).max(255).openapi({ example: 'student.firstName' }),
  type: z.enum(['string', 'number', 'date', 'boolean', 'array', 'object']).openapi({ example: 'string' }),
  required: z.boolean(),
  defaultValue: z.unknown().nullable(),
  description: z.string().max(1000).openapi({ example: 'The student legal first name' }),
  source: z.string().max(500).nullable().openapi({ example: 'student.firstName' }),
}).openapi('TemplateVariable', { description: 'A variable declaration for template binding' })

export const TemplateSchema = z.object({
  id: TemplateId,
  name: z.string().min(1).max(255).openapi({ example: 'IEP Cover Page' }),
  description: z.string().max(2000).openapi({ example: 'Standard IEP cover page with student info, meeting date, and team roster' }),
  documentType: DocumentTypeSchema,
  locale: z.string().min(2).max(10).openapi({ example: 'en-US' }),
  engine: z.enum(['handlebars', 'liquid', 'ejs', 'docx-templates']).openapi({ example: 'handlebars' }),
  content: z.string().min(1).openapi({ description: 'Template content or storage reference' }),
  variables: z.array(TemplateVariableSchema),
  defaultLocale: z.string().min(2).max(10).openapi({ example: 'en-US' }),
  versions: z.record(z.string(), z.string()).openapi({ description: 'Locale to template ID mapping for translated variants' }),
}).openapi('Template', { description: 'A document template with variable bindings and locale variants' })

export const DocumentSchema = z.object({
  id: DocumentId,
  tenantId: z.string().min(1).max(64).openapi({ example: 'dist-wa-12345' }),
  type: DocumentTypeSchema,
  classification: DocumentClassificationSchema,
  status: DocumentStatusSchema,
  ownerId: z.string().uuid().openapi({ example: 'user-1234-5678' }),
  subjectId: z.string().uuid().nullable().openapi({ description: 'Student this document is about' }),
  currentVersion: z.number().int().min(0).openapi({ example: 2 }),
  versions: z.array(DocumentVersionSchema),
  metadata: z.record(z.unknown()).nullable().openapi({ description: 'Document-level metadata (author, team, dates)' }),
  retentionPolicy: z.string().min(1).max(255).openapi({ example: 'iep-ferpa-7yr' }),
  legalHold: z.boolean(),
  legalHoldReason: z.string().max(2000).nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
}).openapi('Document', { description: 'A legal document (IEP, evaluation, etc.) with full lifecycle, versioning, and metadata' })

export const DocumentCreateSchema = z.object({
  tenantId: z.string().min(1).max(64),
  type: DocumentTypeSchema,
  classification: DocumentClassificationSchema,
  ownerId: z.string().uuid(),
  subjectId: z.string().uuid().nullable(),
  metadata: z.record(z.unknown()).nullable(),
  retentionPolicy: z.string().min(1).max(255),
  templateId: z.string().uuid().nullable().openapi({ description: 'Optional template for initial content generation' }),
}).openapi('DocumentCreate', { description: 'Input shape for creating a new document' })

export const DocumentUpdateSchema = z.object({
  status: DocumentStatusSchema.optional(),
  classification: DocumentClassificationSchema.optional(),
  metadata: z.record(z.unknown()).nullable().optional(),
  legalHold: z.boolean().optional(),
  legalHoldReason: z.string().max(2000).nullable().optional(),
}).openapi('DocumentUpdate', { description: 'Partial update shape for an existing document' })

export type DocumentType = z.infer<typeof DocumentTypeSchema>
export type DocumentClassification = z.infer<typeof DocumentClassificationSchema>
export type DocumentStatus = z.infer<typeof DocumentStatusSchema>
export type DocumentVersionStatus = z.infer<typeof DocumentVersionStatusSchema>
export type DocumentVersion = z.infer<typeof DocumentVersionSchema>
export type TemplateVariable = z.infer<typeof TemplateVariableSchema>
export type Template = z.infer<typeof TemplateSchema>
export type Document = z.infer<typeof DocumentSchema>
export type DocumentCreate = z.infer<typeof DocumentCreateSchema>
export type DocumentUpdate = z.infer<typeof DocumentUpdateSchema>
